import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogContent,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import styles from './AddTrainerDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { isValidEmail, isValidPhoneNumber } from '../../utils/utils';
import InputField from '../atoms/InputField';

import type { Trainer, WithOptionalId } from '../../types';
import { useSessionData } from '../../providers';

export interface AddTrainerDialogProps {
  onClose: () => void;
  onSubmit: (
    newTrainer: WithOptionalId<Trainer>,
  ) => Promise<{ success: boolean; emailAlreadyInUse?: boolean }>;
  isOpen: boolean;
}

const initialTrainerData = {
  firstName: '',
  lastName: '',
  email: '',
  emailVerification: '',
  phoneNumber: '',
  subscription: '',
};

const AddTrainerDialog: React.FC<AddTrainerDialogProps> = ({
  isOpen,
  onSubmit,
  onClose,
}) => {
  const { gym } = useSessionData();
  const [trainerData, setTrainerData] = useState(initialTrainerData);

  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [lastNameError, setLastNameError] = useState<boolean>(false);
  const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [emailVerificationError, setEmailVerificationError] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setTrainerData(initialTrainerData);
      setFirstNameError(false);
      setLastNameError(false);
      setPhoneNumberError(false);
      setEmailError(false);
      setEmailErrorMessage('');
      setEmailVerificationError(false);
      setLoading(false);
      setErrorMessage(null);
      setIsCloseConfirmationOpen(false);
    }
  }, [isOpen]);

  const hasChanges = () =>
    trainerData.firstName !== initialTrainerData.firstName ||
    trainerData.lastName !== initialTrainerData.lastName ||
    trainerData.email !== initialTrainerData.email ||
    trainerData.emailVerification !== initialTrainerData.emailVerification ||
    trainerData.phoneNumber !== initialTrainerData.phoneNumber;

  const handleRequestClose = () => {
    if (hasChanges()) {
      setIsCloseConfirmationOpen(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setIsCloseConfirmationOpen(false);
    onClose();
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    const normalizedEmail = trainerData.email.trim();
    const normalizedEmailVerification = trainerData.emailVerification.trim();
    const isEmailInvalid = !isValidEmail(normalizedEmail);
    const normalizedPhoneNumber = trainerData.phoneNumber.trim();
    const validationErrors = {
      firstName: !trainerData.firstName,
      lastName: !trainerData.lastName,
      email: isEmailInvalid,
      emailVerification: normalizedEmail !== normalizedEmailVerification,
      phoneNumber:
        normalizedPhoneNumber.length > 0 &&
        !isValidPhoneNumber(normalizedPhoneNumber),
    };

    setFirstNameError(validationErrors.firstName);
    setLastNameError(validationErrors.lastName);
    setEmailError(validationErrors.email);
    setEmailErrorMessage(
      validationErrors.email ? 'Please enter a valid email address.' : '',
    );
    setEmailVerificationError(validationErrors.emailVerification);
    setPhoneNumberError(validationErrors.phoneNumber);

    if (Object.values(validationErrors).some(Boolean)) return;

    setLoading(true);

    const newTrainer: WithOptionalId<Trainer> = {
      phoneNumber: trainerData.phoneNumber,
      email: normalizedEmail,
      firstName: trainerData.firstName,
      lastName: trainerData.lastName,
      gymIds: [gym?.id || ''],
      isActive: true,
      groupIds: [],
      isManager: false,
      trainingRoutineTemplateIds: [],
      exerciseIds: [],
      memberIds: [],
    };

    try {
      const result = await onSubmit(newTrainer);
      if (result.success) {
        setTrainerData(initialTrainerData);
        onClose();
      } else if (result.emailAlreadyInUse) {
        setEmailError(true);
        setEmailErrorMessage('Email already in use.');
      } else {
        setErrorMessage('An error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BaseDialog
        onClose={handleRequestClose}
        isOpen={isOpen}
        title="Add A Trainer"
        actions={
          <Button
            className={styles.submit}
            variant="outlined"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="primary" />
            ) : (
              <>
                <AddBoxOutlinedIcon color="primary" />
                <span className={styles.submitText}>Add Trainer</span>
              </>
            )}
          </Button>
        }
      >
        <DialogContent className={styles.trainerDataDialog}>
          <InputField
            icon={<PersonOutlineOutlinedIcon color="primary" />}
            title="First Name"
            inputName="firstName"
            autoComplete="firstName"
            value={trainerData.firstName}
            onChange={(firstName) => {
              setFirstNameError(false);
              setTrainerData((prevTrainerData) => ({
                ...prevTrainerData,
                firstName,
              }));
            }}
            error={firstNameError}
            validationMessage="Please enter a first name."
          />
          <InputField
            icon={<PersonOutlineOutlinedIcon color="primary" />}
            title="Last Name"
            inputName="lastName"
            autoComplete="lastName"
            value={trainerData.lastName}
            onChange={(lastName) => {
              setLastNameError(false);
              setTrainerData((prevTrainerData) => ({
                ...prevTrainerData,
                lastName,
              }));
            }}
            error={lastNameError}
            validationMessage="Please enter a last name."
          />
          <InputField
            icon={<MailOutlineRoundedIcon color="primary" />}
            title="Email"
            inputName="email"
            autoComplete="email"
            value={trainerData.email}
            onChange={(email) => {
              setEmailError(false);
              setEmailErrorMessage('');
              setEmailVerificationError(false);
              setTrainerData((prevTrainerData) => ({
                ...prevTrainerData,
                email,
              }));
            }}
            onBlur={() => {
              const isInvalid = !isValidEmail(trainerData.email);
              setEmailError(isInvalid);
              setEmailErrorMessage(
                isInvalid ? 'Please enter a valid email address.' : '',
              );
            }}
            error={emailError}
            validationMessage={emailErrorMessage}
          />
          <InputField
            icon={<MailOutlineRoundedIcon color="primary" />}
            title="Email Verification"
            inputName="emailVerification"
            autoComplete="email"
            value={trainerData.emailVerification}
            onChange={(emailVerification) => {
              setEmailVerificationError(false);
              setTrainerData((prevTrainerData) => ({
                ...prevTrainerData,
                emailVerification,
              }));
            }}
            onBlur={() =>
              setEmailVerificationError(
                trainerData.email !== trainerData.emailVerification,
              )
            }
            error={emailVerificationError}
            validationMessage="Emails do not match."
          />
          <InputField
            icon={<LocalPhoneOutlinedIcon color="primary" />}
            title="Phone Number"
            inputName="phoneNumber"
            autoComplete="phone"
            value={trainerData.phoneNumber}
            onChange={(phoneNumber) => {
              // Allow only digits and a leading '+'
              const sanitized = phoneNumber
                .replace(/[^\d+]/g, '')
                .replace(/(?!^)\+/g, '');
              setPhoneNumberError(false);
              setTrainerData((prevTrainerData) => ({
                ...prevTrainerData,
                phoneNumber: sanitized,
              }));
            }}
            error={phoneNumberError}
            validationMessage="Please enter a valid phone number."
          />
        </DialogContent>
      </BaseDialog>
      <ConfirmationDialog
        isOpen={isCloseConfirmationOpen}
        onCancel={() => setIsCloseConfirmationOpen(false)}
        onSubmit={() => confirmClose()}
        title="Discard changes?"
        text="You have unsaved changes. Do you want to discard them?"
        submitText="Discard"
        cancelText="Keep Editing"
      />
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => setErrorMessage(null)}
        >
          <Alert onClose={() => setErrorMessage(null)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AddTrainerDialog;
